import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { UserAction } from '../api/v4/models/CommonUtilities/user-action';
import { ApplicationConfigResponse } from '../api/v4/models/Models/v4/Application/application-config-response';
import { environment } from '../../environments/environment';
import { EmploymentStatus } from '../api/v4/models/Models/v4/Application/EmploymentDetails/employment-status';
import { EmploymentBasis } from '../api/v4/models/CommonUtilities/employment-basis';
import { UnemploymentStatusEnum } from '../api/v4/models/Models/v4/Application/EmploymentDetails/unemployment-status-enum';
import { Router } from '@angular/router';

export { EmploymentStatus, EmploymentBasis, UnemploymentStatusEnum };

export const ApplicationType = {
  Personal: 'personal',
  Business: 'business'
} as const;

export const ResidencyStatus = {
  NZCitizenOrPermanentResident: 'nz-citizen-or-permanent-resident',
  WorkVisa: 'work-visa',
  StudentOrVisitorVisa: 'student-or-visitor-visa',
  OtherStatus: 'other-status'
} as const;

export const ServiceabilityOption = {
  Comfortable: 'comfortable',
  Manageable: 'manageable',
  Tight: 'tight',
  Cannot: 'cannot'
} as const;

export const CreditHistory = {
  VeryGood: 'very-good',
  Good: 'good',
  Mixed: 'mixed',
  Poor: 'poor',
  NotSure: 'not-sure'
} as const;

export type ApplicationTypeValue = typeof ApplicationType[keyof typeof ApplicationType];
export type ResidencyStatusValue = typeof ResidencyStatus[keyof typeof ResidencyStatus];
export type ServiceabilityOptionValue = typeof ServiceabilityOption[keyof typeof ServiceabilityOption];
export type CreditHistoryValue = typeof CreditHistory[keyof typeof CreditHistory];

export interface EligibilityFormValue {
  applicationType: ApplicationTypeValue | null;
  isOver18: UserAction | null;
  residencyStatus: ResidencyStatusValue | null;
  vehiclePrice: number | null;
  accessories: number | null;
  deposit: number | null;
  tradeIn: number | null;
  employmentStatus: EmploymentStatus | 'Other' | null;
  employmentBasis: EmploymentBasis | null;
  unemploymentStatus: UnemploymentStatusEnum | 'Student' | null;
  serviceabilityOption: ServiceabilityOptionValue | null;
  creditHistory: CreditHistoryValue | null;
  hasCreditIssues: UserAction | null;
  loanAmount: number | null;
}

export interface EligibilityForm {
  applicationType: FormControl<ApplicationTypeValue | null>;
  isOver18: FormControl<UserAction | null>;
  residencyStatus: FormControl<ResidencyStatusValue | null>;
  vehiclePrice: FormControl<number | null>;
  accessories: FormControl<number | null>;
  deposit: FormControl<number | null>;
  tradeIn: FormControl<number | null>;
  employmentStatus: FormControl<EmploymentStatus | 'Other' | null>;
  employmentBasis: FormControl<EmploymentBasis | null>;
  unemploymentStatus: FormControl<UnemploymentStatusEnum | 'Student' | null>;
  serviceabilityOption: FormControl<ServiceabilityOptionValue | null>;
  creditHistory: FormControl<CreditHistoryValue | null>;
  hasCreditIssues: FormControl<UserAction | null>;
}

export const ELIGIBILITY_PREFILL_KEY = 'eligibility_prefill';

// Repayment calculation constants — update these to change rates/fees across the form and disclaimer
const ANNUAL_RATE_PCT = 13.95;
const MONTHLY_RATE = ANNUAL_RATE_PCT / 100 / 12;
const LOAN_TERM_MONTHS = 60;
const ESTABLISHMENT_FEE = 510;
const MONTHLY_ADMIN_FEE = 2.50;
const MIN_LOAN_AMOUNT = 2000;

interface ApplicationConfigBaseResponse {
  entityData?: ApplicationConfigResponse;
}

@Injectable({
  providedIn: 'root'
})
export class EligibilityService {
  readonly annualInterestRatePct = ANNUAL_RATE_PCT;
  readonly loanTermMonths = LOAN_TERM_MONTHS;
  readonly establishmentFee = ESTABLISHMENT_FEE;
  readonly monthlyAdminFee = MONTHLY_ADMIN_FEE;

  private _eligibilityForm: FormGroup<EligibilityForm>;
  applicationConfigResponse: ApplicationConfigResponse = {};
  isLoading = true;

  get eligibilityForm(): FormGroup<EligibilityForm> {
    return this._eligibilityForm;
  }

  constructor(private formBuilder: FormBuilder, private http: HttpClient, private router: Router) {
    this._eligibilityForm = this.createForm();
  }

  public applyNow(): void {
    this.saveToLocalStorage();
    this.router.navigate(['/'], { queryParamsHandling: 'preserve' });
  }

  private createForm(): FormGroup<EligibilityForm> {
    return this.formBuilder.group<EligibilityForm>({
      applicationType: this.formBuilder.control<ApplicationTypeValue | null>(
        null,
        { validators: [Validators.required] }
      ),
      isOver18: this.formBuilder.control<UserAction | null>(
        null,
        { validators: [Validators.required] }
      ),
      residencyStatus: this.formBuilder.control<ResidencyStatusValue | null>(
        null,
        { validators: [Validators.required] }
      ),
      vehiclePrice: this.formBuilder.control<number | null>(null),
      accessories: this.formBuilder.control<number | null>(null),
      deposit: this.formBuilder.control<number | null>(null),
      tradeIn: this.formBuilder.control<number | null>(null),
      employmentStatus: this.formBuilder.control<EmploymentStatus | 'Other' | null>(
        null,
        { validators: [Validators.required] }
      ),
      employmentBasis: this.formBuilder.control<EmploymentBasis | null>(null),
      unemploymentStatus: this.formBuilder.control<UnemploymentStatusEnum | 'Student' | null>(null),
      serviceabilityOption: this.formBuilder.control<ServiceabilityOptionValue | null>(
        null,
        { validators: [Validators.required] }
      ),
      creditHistory: this.formBuilder.control<CreditHistoryValue | null>(
        null,
        { validators: [Validators.required] }
      ),
      hasCreditIssues: this.formBuilder.control<UserAction | null>(null)
    });
  }

  loadConfig(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const publicId = urlParams.get('publicId') ?? '';
    const dealershipCode = urlParams.get('dealershipCode') ?? '';
    const origin = urlParams.get('origin') ?? '';

    this.http.get<ApplicationConfigBaseResponse>(
      `${environment.apiUrl}/v4/public/application/config`,
      {
        headers: { 'X-Limelight-ClientId': environment.apiHeaderId },
        params: { publicAccountId: publicId, dealershipShortcode: dealershipCode, origin }
      }
    ).subscribe({
      next: (response) => {
        if (response.entityData) {
          this.applicationConfigResponse = response.entityData;
          if (response.entityData.hasEligibilityForm === false) {
            this.applyNow();
            return;
          }
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('applicationConfig error', err);
        this.isLoading = false;
      }
    });
  }

  get loanAmount(): number {
    const c = this._eligibilityForm.controls;
    const price = c.vehiclePrice.value ?? 0;
    const acc = c.accessories.value ?? 0;
    const dep = c.deposit.value ?? 0;
    const trade = c.tradeIn.value ?? 0;
    return Math.max(0, price + acc - dep - trade);
  }

  get weeklyRepayment(): number {
    const loan = this.loanAmount;
    if (loan < MIN_LOAN_AMOUNT) return 0;
    const pv = loan + ESTABLISHMENT_FEE;
    const pmt = pv * MONTHLY_RATE / (1 - Math.pow(1 + MONTHLY_RATE, -LOAN_TERM_MONTHS));
    return (pmt + MONTHLY_ADMIN_FEE) * 12 / 52;
  }

  get totalAmountRepayable(): number {
    const loan = this.loanAmount;
    if (loan < MIN_LOAN_AMOUNT) return 0;
    const pv = loan + ESTABLISHMENT_FEE;
    const pmt = pv * MONTHLY_RATE / (1 - Math.pow(1 + MONTHLY_RATE, -LOAN_TERM_MONTHS));
    return (pmt + MONTHLY_ADMIN_FEE) * LOAN_TERM_MONTHS;
  }

  get employmentScore(): number {
    const status = this._eligibilityForm.controls.employmentStatus.value;
    const basis = this._eligibilityForm.controls.employmentBasis.value;
    const unemployment = this._eligibilityForm.controls.unemploymentStatus.value;

    if (status === EmploymentStatus.Employed || status === EmploymentStatus.SelfEmployed) {
      switch (basis) {
        case EmploymentBasis.FullTime: return 3;
        case EmploymentBasis.PartTime: return 2;
        case EmploymentBasis.Contract: return 2;
        case EmploymentBasis.Casual: return 1;
        case EmploymentBasis.Seasonal: return 1;
        default: return 0;
      }
    }
    if (status === EmploymentStatus.Unemployed) {
      switch (unemployment) {
        case UnemploymentStatusEnum.SelfFunding: return 2;
        case UnemploymentStatusEnum.Superannuation: return 2;
        case UnemploymentStatusEnum.Beneficiary: return 1;
        case 'Student': return 1;
        default: return 0;
      }
    }
    if (status === 'Other') return 1;
    return 0;
  }

  get serviceabilityScore(): number {
    switch (this._eligibilityForm.controls.serviceabilityOption.value) {
      case ServiceabilityOption.Comfortable: return 3;
      case ServiceabilityOption.Manageable: return 2;
      case ServiceabilityOption.Tight: return 1;
      case ServiceabilityOption.Cannot: return 0;
      default: return 0;
    }
  }

  get creditHistoryScore(): number {
    switch (this._eligibilityForm.controls.creditHistory.value) {
      case CreditHistory.VeryGood: return 3;
      case CreditHistory.Good: return 2;
      case CreditHistory.Mixed: return 1;
      case CreditHistory.Poor: return 0;
      case CreditHistory.NotSure: return 1;
      default: return 0;
    }
  }

  get totalScore(): number {
    return this.employmentScore + this.serviceabilityScore + this.creditHistoryScore;
  }

  get resultBand(): 'likely' | 'hard-to-say' | 'unlikely' {
    const score = this.totalScore;
    if (score >= 7) return 'likely';
    if (score >= 4) return 'hard-to-say';
    return 'unlikely';
  }

  get isPersonal(): boolean {
    return this._eligibilityForm.controls.applicationType.value === ApplicationType.Personal;
  }

  getFormValue(): EligibilityFormValue {
    return {
      ...this._eligibilityForm.getRawValue(),
      loanAmount: this.loanAmount || null
    };
  }

  saveToLocalStorage(): void {
    localStorage.setItem(ELIGIBILITY_PREFILL_KEY, JSON.stringify(this.getFormValue()));
  }

  static readPrefill(): EligibilityFormValue | null {
    try {
      const raw = localStorage.getItem(ELIGIBILITY_PREFILL_KEY);
      return raw ? JSON.parse(raw) as EligibilityFormValue : null;
    } catch {
      return null;
    }
  }

  reset(): void {
    this._eligibilityForm.reset({
      applicationType: null,
      isOver18: null,
      residencyStatus: null,
      vehiclePrice: null,
      accessories: null,
      deposit: null,
      tradeIn: null,
      employmentStatus: null,
      employmentBasis: null,
      unemploymentStatus: null,
      serviceabilityOption: null,
      creditHistory: null,
      hasCreditIssues: null
    });
  }

  getApplicationType(): ApplicationTypeValue | null {
    return this._eligibilityForm.controls.applicationType.value;
  }
}
